import { useState } from "react";

/*
  Selection-based bulk delete used by every document section
  (organization documents, contracts, statements, report files).

  - Every selected item is attempted (allSettled), so one failure no longer
    hides which items were actually deleted.
  - The data is always refreshed afterwards, so the list matches the server.
  - Failed items stay selected and the error is exposed for the confirm modal.
*/
export default function useSelectionDelete({ deleteOne, refresh, lang }) {
  const [active, setActive] = useState(false);
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setActive(false);
    setSelected([]);
    setConfirmOpen(false);
    setError("");
  };

  const toggle = (id) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );

  const confirm = async () => {
    if (!selected.length || deleting) return;
    setDeleting(true);
    setError("");

    const results = await Promise.allSettled(selected.map((id) => deleteOne(id)));
    const failedIds = selected.filter((_, index) => results[index].status === "rejected");

    try {
      await refresh?.();
    } catch (refreshError) {
      console.error(refreshError);
    }

    if (failedIds.length) {
      const firstError = results.find((result) => result.status === "rejected")?.reason;
      console.error(firstError);
      setSelected(failedIds);
      setError(
        lang === "ar"
          ? `تعذّر حذف ${failedIds.length} من ${selected.length}${firstError?.message ? ` — ${firstError.message}` : ""}`
          : `Could not delete ${failedIds.length} of ${selected.length}${firstError?.message ? ` — ${firstError.message}` : ""}`,
      );
    } else {
      reset();
    }
    setDeleting(false);
  };

  return {
    active,
    selected,
    confirmOpen,
    deleting,
    error,
    isSelected: (id) => selected.includes(id),
    toggle,
    start: () => {
      setActive(true);
      setError("");
    },
    cancel: reset,
    openConfirm: () => {
      setError("");
      setConfirmOpen(true);
    },
    closeConfirm: () => setConfirmOpen(false),
    confirm,
  };
}
